import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import StarRating from '../components/StarRating';
import './ThemeDetailPage.css';

const ThemeDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [theme, setTheme] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userRating, setUserRating] = useState(0);
  const [hasUpvoted, setHasUpvoted] = useState(false);

  useEffect(() => {
    // Mock data - In production, this would fetch from the backend API
    const mockThemes = {
      1: {
        id: 1,
        title: 'No live chat support available',
        site: 'Airbnb',
        description: 'Airbnb lacks real-time chat support, making it difficult to resolve urgent booking issues or get immediate assistance during stays.',
        fullDescription: 'Airbnb currently does not offer a live chat support feature, which is a significant gap in their customer service offerings. Users who encounter urgent issues during their stays or while booking have to rely on phone support with long wait times or email support which can take days to receive a response. This is particularly frustrating for time-sensitive matters such as check-in issues, safety concerns, or payment problems. Many competitors in the vacation rental space have implemented live chat with much better customer satisfaction ratings.',
        upvotes: 342,
        averageRating: 4.2,
        reviewCount: 87,
        category: 'Customer Service',
        severity: 'High',
        reportedDate: '2023-11-15'
      },
      2: {
        id: 2,
        title: 'Automated responses only',
        site: 'Kayak',
        description: 'Customer service relies heavily on automated responses with no option to speak with a human agent for complex travel issues.',
        fullDescription: 'Kayak\'s customer support system is primarily automated, which creates significant challenges for users with complex or unique travel situations. The automated system cannot handle nuanced questions about multi-city bookings, complex refund scenarios, or special accommodation needs. Users report frustration with being unable to escalate issues to human agents, leading to unresolved problems and lost bookings.',
        upvotes: 218,
        averageRating: 3.8,
        reviewCount: 64,
        category: 'Customer Service',
        severity: 'Medium',
        reportedDate: '2023-10-22'
      },
      3: {
        id: 3,
        title: 'Long wait times for phone support',
        site: 'Airbnb',
        description: 'Phone support wait times exceed 2 hours during peak times, making it impractical for urgent matters.',
        fullDescription: 'During peak travel seasons and weekends, Airbnb\'s phone support wait times can exceed 2 hours. This creates serious problems for guests and hosts dealing with emergency situations such as lockouts, safety issues, or urgent cancellations. The company has not scaled their support team to match their growing user base, leading to increasingly poor support experiences.',
        upvotes: 456,
        averageRating: 4.5,
        reviewCount: 123,
        category: 'Response Time',
        severity: 'Critical',
        reportedDate: '2023-12-01'
      }
    };

    setTimeout(() => {
      setTheme(mockThemes[id]);
      setLoading(false);
    }, 500);
  }, [id]);

  const handleUpvote = () => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setHasUpvoted(!hasUpvoted);
    setTheme(prev => ({
      ...prev,
      upvotes: hasUpvoted ? prev.upvotes - 1 : prev.upvotes + 1
    }));
  };

  const handleRating = (rating) => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    setUserRating(rating);
    // In production, this would save to the backend
    console.log('User rated:', rating);
  };

  if (loading) {
    return <div className="loading">Loading theme details...</div>;
  }

  if (!theme) {
    return (
      <div className="error-page">
        <h2>Theme not found</h2>
        <button onClick={() => navigate('/')} className="btn btn-primary">
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="theme-detail-page">
      <button onClick={() => navigate('/')} className="back-btn">
        ← Back to Themes
      </button>

      <div className="theme-detail-header">
        <div className="theme-title-row">
          <h2>{theme.title}</h2>
          <span className="theme-site-badge">{theme.site}</span>
        </div>
        <div className="theme-meta">
          <span className="meta-item">
            <strong>Category:</strong> {theme.category}
          </span>
          <span className="meta-item">
            <strong>Severity:</strong> 
            <span className={`severity ${theme.severity.toLowerCase()}`}>
              {theme.severity}
            </span>
          </span>
          <span className="meta-item">
            <strong>Reported:</strong> {new Date(theme.reportedDate).toLocaleDateString()}
          </span>
        </div>
      </div>

      <div className="theme-content">
        <div className="description-section">
          <h3>Description</h3>
          <p>{theme.fullDescription || theme.description}</p>
        </div>

        <div className="stats-section">
          <div className="stat-card">
            <h4>Community Votes</h4>
            <div className="vote-content">
              <span className="vote-count">{theme.upvotes}</span>
              <button 
                onClick={handleUpvote}
                className={`upvote-btn ${hasUpvoted ? 'active' : ''}`}
              >
                {hasUpvoted ? '👍 Upvoted' : '👍 Upvote'}
              </button>
            </div>
          </div>

          <div className="stat-card">
            <h4>Average Rating</h4>
            <div className="rating-content">
              <StarRating rating={theme.averageRating} readOnly />
              <span className="review-count">{theme.reviewCount} reviews</span>
            </div>
          </div>
        </div>

        <div className="user-rating-section">
          <h3>Your Experience</h3>
          <p>How much did this theme affect your experience?</p>
          <StarRating 
            rating={userRating} 
            onRate={handleRating}
          />
          {userRating > 0 && (
            <p className="rating-feedback">
              Thank you for your rating!
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default ThemeDetailPage;
